<template>
  <div ref="getting_started_article" class="library__article">
    <div v-if="!isUserGuide">
      <p>
        <VisibleText>Go to</VisibleText>
        <router-link to="/library">
          <VisibleText> Library</VisibleText>
        </router-link>
      </p>
    </div>
    <h1 class="library__h1">
      <VisibleText>Getting started</VisibleText>
    </h1>
    <div class="library__heading-line"></div>
    <p>
      <VisibleText>Welcome to ResearchAI! Let's go from data to insights in clicks. </VisibleText>
    </p>
    <h2 class="library__h2"><VisibleText>What is ResearchAI?</VisibleText></h2>
    <div class="library__heading-line"></div>
    <p>
      <VisibleText
        >ResearchAI is a tool where you can upload organizational data and get directed towards
        industry reports in clicks.
      </VisibleText>
    </p>
    <h3><VisibleText>What kind of data?</VisibleText></h3>
    <p>
      <VisibleText>
        Many leading organizations collect information like customer interactions or employee
        experiences from surveys, CRM software, customer complaints forms/emails, and more.
      </VisibleText>
    </p>
    <h3><VisibleText>Why ResearchAI?</VisibleText></h3>
    <p>
      <VisibleText>
        Unlocking insights from these can be expensive, time-consuming or require expertise.
      </VisibleText>
    </p>
    <p>
      <VisibleText>
        ResearchAI tries to make analysis accessible. Find delighted or upset segments. Predict
        important outcomes using drivers analysis. Find hidden themes in your text using our
        qualitative research tools. Then, share your reports with colleagues or customers.
      </VisibleText>
    </p>
    <h3 style="text-align: center">
      <VisibleText>The process from start to finish</VisibleText>
    </h3>
    <div class="figure__horizontal">
      <div class="figure__horizontal__outside-square">
        <div class="figure__horizontal__outside-square__text">
          <VisibleText>Dataset View</VisibleText>
        </div>
        <div class="figure__horizontal--square">
          <VisibleText>Upload data</VisibleText>
        </div>
        <div class="figure__horizontal--arrow">→</div>
        <div class="figure__horizontal--square">
          <VisibleText>Clean data</VisibleText>
        </div>
      </div>
      <div class="figure__horizontal__outside-square">
        <div class="figure__horizontal--arrow">→</div>
        <div class="figure__horizontal__outside-square__text">
          <VisibleText> Project View </VisibleText>
        </div>
        <div class="figure__horizontal--square">
          <VisibleText>Analyze</VisibleText>
        </div>
        <div class="figure__horizontal--arrow">→</div>
        <div class="figure__horizontal--square">
          <VisibleText>Save and share</VisibleText>
        </div>
      </div>
    </div>
    <p><VisibleText>Learn more about</VisibleText>:</p>
    <ul class="library__list">
      <li>
        <a
          href="javascript:void(0)"
          v-if="isUserGuide"
          @click="$emit('new-page', 'UploadingFiles')"
          onclick="this.style.color = '#3d0071'"
        >
          <VisibleText> Uploading your data </VisibleText>
        </a>
        <div v-else>
          <router-link to="/library/guide/uploading-files-into-researchai">
            <VisibleText>Uploading your data</VisibleText>
          </router-link>
        </div>
      </li>
      <li>
        <a
          href="javascript:void(0)"
          v-if="isUserGuide"
          @click="$emit('new-page', 'BasicAnalysis')"
          onclick="this.style.color = '#3d0071'"
        >
          <VisibleText> Conducting basic analysis </VisibleText>
        </a>
        <div v-else>
          <router-link to="/library/guide/basic-analysis-in-researchai">
            <VisibleText> Conducting basic analysis </VisibleText>
          </router-link>
        </div>
      </li>
      <li>
        <a
          href="javascript:void(0)"
          v-if="isUserGuide"
          @click="$emit('new-page', 'DriversAnalysis')"
          onclick="this.style.color = '#3d0071'"
        >
          <VisibleText> What is drivers analysis? </VisibleText>
        </a>
        <div v-else>
          <router-link to="/library/guide/drivers-analysis-in-researchai">
            <VisibleText> What is drivers analysis? </VisibleText>
          </router-link>
        </div>
      </li>
      <li>
        <a
          href="javascript:void(0)"
          v-if="isUserGuide"
          @click="$emit('new-page', 'BenchmarkingAnalysis')"
          onclick="this.style.color = '#3d0071'"
        >
          <VisibleText> What is benchmarking analysis? </VisibleText>
        </a>
        <div v-else>
          <router-link to="/library/guide/what-is-benchmarking-in-researchai">
            <VisibleText> What is benchmarking analysis? </VisibleText>
          </router-link>
        </div>
      </li>
      <li>
        <a
          href="javascript:void(0)"
          v-if="isUserGuide"
          @click="$emit('new-page', 'ThematicAnalysis')"
          onclick="this.style.color = '#3d0071'"
        >
          <VisibleText>What is thematic analysis?</VisibleText>
        </a>
        <div v-else>
          <router-link to="/library/guide/what-is-thematic-analysis-in-researchai">
            <VisibleText> What is thematic analysis? </VisibleText>
          </router-link>
        </div>
      </li>
      <li>
        <a
          href="javascript:void(0)"
          v-if="isUserGuide"
          @click="$emit('new-page', 'OutliersAnalysis')"
          onclick="this.style.color = '#3d0071'"
        >
          <VisibleText> What is outliers analysis? </VisibleText>
        </a>
        <div v-else>
          <router-link to="/library/guide/outliers-analysis-in-researchai">
            <VisibleText> What is outliers analysis? </VisibleText>
          </router-link>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue';
import { computed, reactive } from 'vue';
export default {
  name: 'getting_started',
  setup() {
    const siteData = reactive({
      title: 'Getting started with ResearchAI',
      description: "A guide for getting started with PigeonLine's ResearchAI",
    });
    useHead({
      title: computed(() => siteData.title),
      meta: [
        {
          name: 'description',
          content: computed(() => siteData.description),
        },
      ],
    });
  },
  props: {
    isUserGuide: { type: Boolean },
  },
};
</script>
